import { aiOpenapiClient, unwrapResult } from '@/api/openapi-client';
import { env } from '@/env';
import { useCanUseAi } from '@/features/ai-assistant/hooks/useCanUseAi';
import {
  useAiChangeset,
  type AiChangesetAdapter,
} from '@/features/ai-changeset/hooks/useAiChangeset';
import { changesetApi } from '@/features/ai-changeset/utils/changeset-api';
import {
  buildPromotionsDraft,
  promotionChangeLabels,
  summarizePromotionsDraft,
} from '@/features/promotions/utils/promotion-draft';
import { useCan } from '@/hooks/useAbility';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { Promotion } from '@restaurant/api-client';
import {
  PromotionChangesetStaleSchema,
  PromotionChangesSchema,
  PromotionsChangesetAppliedSchema,
  PromotionsQuickEditSessionSchema,
  type PromotionChange,
  type PromotionConflict,
} from '@restaurant/api-contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

export function useCanUsePromotionsAi(): boolean {
  const canManage = useCan('manage', 'Promotion');
  return useCanUseAi() && canManage;
}

export function usePromotionsQuickEdit(
  listed: Promotion[] | undefined,
  all: Promotion[] | undefined,
) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const canUseAi = useCanUsePromotionsAi();
  const summaryCopy = useT().promotionsQuickEdit.summary;
  const qc = useQueryClient();
  const params = { restaurantId };
  const conflictsKey = ['promotion-conflicts', restaurantId] as const;
  const { data: drafted } = useQuery({
    queryKey: conflictsKey,
    queryFn: (): Promise<{ changes: string; conflicts: PromotionConflict[] }> =>
      Promise.resolve({ changes: '', conflicts: [] }),
    enabled: false,
  });

  const adapter = useMemo<AiChangesetAdapter<PromotionChange>>(
    () => ({
      targets: (changes) => buildPromotionsDraft(listed ?? [], all ?? [], changes).targets,
      labels: (changes) => promotionChangeLabels(all ?? [], changes),
      summary: (changes) => summarizePromotionsDraft(changes, summaryCopy),
    }),
    [listed, all, summaryCopy],
  );

  const loadSession = (before: string | undefined) =>
    aiOpenapiClient.GET('/restaurants/{restaurantId}/ai/promotions/quick-edit', {
      params: { path: params, query: { before } },
    });
  const api = changesetApi<PromotionChange, unknown>(
    {
      sessionSchema: PromotionsQuickEditSessionSchema,
      staleSchema: PromotionChangesetStaleSchema,
      appliedSchema: PromotionsChangesetAppliedSchema,
    },
    {
      load: loadSession,
      apply: (turnId) =>
        aiOpenapiClient.POST(
          '/restaurants/{restaurantId}/ai/promotions/quick-edit/turns/{turnId}/apply',
          { params: { path: { ...params, turnId } } },
        ),
      undo: (turnId) =>
        aiOpenapiClient.POST(
          '/restaurants/{restaurantId}/ai/promotions/quick-edit/turns/{turnId}/undo',
          { params: { path: { ...params, turnId } } },
        ),
      discard: (turnId) =>
        aiOpenapiClient.POST(
          '/restaurants/{restaurantId}/ai/promotions/quick-edit/turns/{turnId}/discard',
          { params: { path: { ...params, turnId } } },
        ),
    },
  );

  const quickEdit = useAiChangeset<PromotionChange>({
    changesSchema: PromotionChangesSchema,
    id: `promotions:${restaurantId}`,
    enabled: canUseAi && !!restaurantId && !!all,
    turnUrl: `${env.aiUrl}/restaurants/${restaurantId}/ai/promotions/quick-edit`,
    pageName: 'promotions',
    fullViewLabel: null,
    viewRows: 5,
    api: {
      ...api,
      load: async (before) => {
        const data = PromotionsQuickEditSessionSchema.parse(
          unwrapResult(await loadSession(before)),
        );
        if (!before) {
          qc.setQueryData(conflictsKey, {
            changes: JSON.stringify(data.session?.draft?.changes ?? []),
            conflicts: data.conflicts,
          });
        }
        return data.session;
      },
    },
    onApplySettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['promotions', restaurantId] }),
        qc.invalidateQueries({ queryKey: ['promotion', restaurantId] }),
      ]),
    adapter,
  });

  const draft = useMemo(() => {
    const current = drafted?.changes === JSON.stringify(quickEdit.changes);
    const conflicts = current ? (drafted?.conflicts ?? []) : [];
    return buildPromotionsDraft(listed ?? [], all ?? [], quickEdit.changes, conflicts);
  }, [listed, all, quickEdit.changes, drafted]);

  return { ...quickEdit, draft, canUseAi };
}
